import React from "react";
import Navbar from "../components/navbar";
import Footer from "../components/footer";
import Hero from "../components/hero";
import Accordion from "../components/accordion";
import individualIcon from "../assets/icons/individual.svg";
import virtualIcon from "../assets/icons/virtual.svg";
import psychometricIcon from "../assets/icons/psychometric.svg";
import subjectChoiceIcon from "../assets/icons/subjectchoice.svg";
import careerIcon from "../assets/icons/career.svg";
import Equestrian from "../assets/icons/equestrian.svg";
import "../styles/universal.css";
import "../styles/servicePage.css";

const ServicePage = () => {
  return (
    <div className="service-page">
      <Navbar />
      <Hero
        title="Services"
        subtitle="I offer compassionate counselling services, insightful psychometric assessments, and performance focused sport psychology to help you grow and thrive."
      ></Hero>
      <div className="service-underlay">
        <Accordion
          sections={[
            {
              title: "Individual Counselling",
              blockquote:
                "We think we listen, but very rarely do we listen with real understanding, true empathy. Yet listening, of this very special kind, is one of the most potent forces for change that I know.",
              blockquoteAuthor: "Carl Rogers",
              content:
                "Individual counselling provides a confidential, supportive space to explore your personal challenges, emotions, and life transitions. Sessions are tailored to your unique needs and may support concerns such as anxiety, trauma, bereavement, grief, self-esteem, emotional regulation, and adjustment difficulties, using a strength-based and collaborative approach.",
              image: individualIcon,
            },
            {
              title: "Virtual Counselling",
              blockquote:
                "You are stronger than you seem, braver than you believe, and smarter than you think.",
              blockquoteAuthor: "A.A. Milne",
              content:
                "Virtual counselling offers flexible and accessible mental health support through secure online sessions. This option is well-suited for individuals who prefer remote support or are unable to attend in-person sessions. Virtual counselling sessions are conducted over Google Meet. Clients who choose to utilise virtual counselling sessions are required to have a private space from which to conduct their sessions, as well as a stable internet connection.",
              image: virtualIcon,
            },
            {
              title: "Psychometric Assessments",
              blockquote:
                "Out of your vulnerabilities will come your strength.",
              blockquoteAuthor: "Sigmund Freud",
              content:
                "A psychometric assessment is a tool used to objectively measure an individual's mental capabilities (aptitude/ability) and behavioral style (personality/traits) for purposes like recruitment, career guidance, and development. Assessments involve an introductory screening session, the assessment, an individual report and feedback session.",
              image: psychometricIcon,
            },
            {
              title: "Subject Choice Assessment",
              blockquote:
                "What you need, above all else, is a love for your subject, whatever it is.",
              blockquoteAuthor: "Neil deGrasse Tyson",
              content:
                "This package is catered for Grade 9 learners, to assist them with choosing Grade 10 subjects which will align with their individual aptitudes, interests and personality traits.",
              image: subjectChoiceIcon,
            },
            {
              title: "Career Assessment",
              blockquote:
                "Choose a job you love, and you will never have to work a day in your life.",
              blockquoteAuthor: "Confucius",
              content:
                "This package is catered to matric learners and adults to assist them with making informed career choices based on individual aptitudes, interests, personality traits, values and experiences.",
              image: careerIcon,
            },
            {
              title: "Ride with Confidence",
              blockquote:
                "The essential joy of being with horses is that it brings us in contact with rare elements of grace, beauty, spirit, and fire.",
              blockquoteAuthor: "Sharon Ralls Lemon",
              content:
                "Are you an equestrian struggling with pre-show jitters, fear after a fall, or performance nerves that hold you back? As both a mental healthcare professional and an experienced equestrian competitor, I understand the unique mental challenges faced by equestrians. Using BrainWorking Recursive Therapy® (BWRT), a fast and effective approach for rewiring unhelpful thought patterns, I can help you build confidence, focus, and calm in the saddle.",
              image: Equestrian,
            },
          ]}
        />
      </div>

      <div className="guide-section">
        <div className="guide-container">
          <h1 className="guide-title">
            Preparing for Your First Counselling Session
          </h1>
          <p className="guide-intro">
            Taking the step to begin counselling is a courageous act of
            self-care. Feeling a mix of hope and nervousness is completely
            normal. This guide will help you feel prepared and empowered for
            your first session.
          </p>

          <div className="guide-content">
            <div className="guide-card">
              <h2>Before Your Session</h2>
              <ul>
                <li>
                  <strong>Complete Paperwork:</strong> Fill out any intake forms
                  sent to you. This saves your session time for conversation.
                </li>
                <li>
                  <strong>Confirm Logistics:</strong> Note the date, time,
                  location (or online link), and any parking instructions.
                </li>
                <li>
                  <strong>Reflect Briefly:</strong> Ask yourself, &quot;What do
                  I hope to get from counselling?&quot; You don&#39;t need a
                  perfect answer, a general direction is a great start.
                </li>
                <li>
                  <strong>Set an Intention:</strong> Remind yourself:
                  &quot;It&#39;s okay to be open, honest, and not have all the
                  answers.&quot;
                </li>
              </ul>
            </div>

            <div className="guide-card">
              <h2>What to Expect During Your Session</h2>
              <p className="guide-subtitle">
                The first session is a first introduction where you and I will
                get to know each other. It is focused on building connection and
                determining wether we will make a good team.
              </p>
              <ul>
                <li>
                  <strong>Welcome & Safety:</strong> I will explain how
                  confidentiality works to create a foundation of trust.
                </li>
                <li>
                  <strong>Sharing Your Story:</strong> You&#39;ll be invited to
                  talk about what brought you in. I will listen and ask
                  supportive questions and what you would like to get out of
                  counselling.
                </li>
                <li>
                  <strong>Collaborative Dialogue:</strong> This is a two-way
                  conversation. Feel free to ask me questions about my approach
                  or what working together might look like.
                </li>
                <li>
                  <strong>Looking Ahead:</strong> We will discuss potential
                  goals and a preliminary plan for moving forward together.
                </li>
              </ul>
            </div>

            <div className="guide-card">
              <h2>Helpful Mindset Tips</h2>
              <ul>
                <li>
                  <strong>You Can't "Fail":</strong> There&#39;s no wrong way to
                  be. Silence, emotion, or uncertainty are all welcome.
                </li>
                <li>
                  <strong>Honesty at Your Pace:</strong> Share what feels
                  comfortable. I am there to meet you where you are.
                </li>
                <li>
                  <strong>The "Fit" is Key:</strong> Pay attention to how you
                  feel. Do you feel heard and safe? It&#39;s okay if you need to
                  find a different counsellor. The right fit is important.
                </li>
              </ul>
            </div>

            <div className="guide-card">
              <h2>After Your Session</h2>
              <ul>
                <li>
                  <strong>Practice Self-Care:</strong> The session may bring up
                  emotions. Plan for some gentle time afterward—a walk,
                  journaling, or a quiet moment.
                </li>
                <li>
                  <strong>Reflect Gently:</strong> Consider how you felt during
                  and after the session, and if you feel hopeful about the
                  journey forward.
                </li>
                <li>
                  <strong>Be Patient with Yourself:</strong> Meaningful change
                  is a process. Beginning is a significant step.
                </li>
              </ul>
            </div>
          </div>

          <blockquote className="guide-closing-quote">
            Remember: This time and space are dedicated entirely to you and your
            wellbeing, exploring who you are, and supporting your growth. I am
            honoured to be a part of your journey.
          </blockquote>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ServicePage;
